import { useMemo, useState } from "react";
import { API, api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  DownloadSimple, Eye, ClipboardText, CurrencyDollar, Users, FirstAidKit, Clock, Certificate, Syringe,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { canFinance } from "@/lib/roles";

const REPORTS = [
  { key: "attendance", title: "Daily attendance", icon: ClipboardText, ranged: true,
    tone: "bg-blue-50 text-blue-600",
    blurb: "Every arrival and departure with hours in care — the record licensing asks for." },
  { key: "subsidy", title: "Voucher / subsidy attendance", icon: Certificate, ranged: true,
    tone: "bg-amber-50 text-amber-600",
    blurb: "Attendance with the guardian signature captured at each check-in, ready for agency audits." },
  { key: "billing", title: "Billing & payments", icon: CurrencyDollar, ranged: true, adminOnly: true,
    tone: "bg-emerald-50 text-emerald-600",
    blurb: "Charges, payments, methods and outstanding balances across any date range." },
  { key: "timecards", title: "Staff timecards & hours", icon: Clock, ranged: true, adminOnly: true,
    tone: "bg-purple-50 text-purple-600",
    blurb: "Clock-in and clock-out times, hours by staff member and overtime above 40 hours." },
  { key: "enrollment", title: "Enrollment & capacity", icon: Users, ranged: false,
    tone: "bg-red-50 text-red-500",
    blurb: "Active students, waitlists, open spots and percentage filled for every room." },
  { key: "immunizations", title: "Immunization records", icon: Syringe, ranged: false,
    tone: "bg-teal-50 text-teal-600",
    blurb: "Every vaccine on file with what's missing or overdue, for licensing health checks." },
  { key: "roster", title: "Roster & emergency info", icon: FirstAidKit, ranged: false,
    tone: "bg-slate-100 text-slate-600",
    blurb: "Allergies, medications, doctor, guardians and emergency contacts for the licensing binder." },
];

const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);

export default function Reports() {
  const { user } = useAuth();
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const shown = REPORTS.filter((r) => !(r.adminOnly && !canFinance(user)));

  const qs = (r) => (r.ranged ? `?start=${start}&end=${end}` : "?");

  const download = (r) => {
    const url = `${API}/reports/${r.key}${qs(r)}${r.ranged ? "&" : ""}format=csv`;
    // Cookie-authenticated download: fetch as a blob, then save it.
    setBusy(r.key);
    api.get(url.replace(API, ""), { responseType: "blob" })
      .then(({ data }) => {
        const href = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = href;
        a.download = `${r.key}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
        toast.success(`${r.title} downloaded`);
      })
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not build that report"))
      .finally(() => setBusy(""));
  };

  const show = async (r) => {
    setBusy(r.key);
    try {
      const { data } = await api.get(`/reports/${r.key}${qs(r)}${r.ranged ? "&" : ""}format=json`);
      setPreview({ report: r, data });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy("");
  };

  const rows = useMemo(() => preview?.data?.rows || [], [preview]);
  const cols = useMemo(
    () => (rows.length ? Object.keys(rows[0]).filter((k) => k !== "student_id") : []), [rows]);
  const totals = useMemo(
    () => Object.entries(preview?.data || {}).filter(([k, v]) =>
      k !== "rows" && k !== "start" && k !== "end" && (typeof v === "number" || typeof v === "string")),
    [preview]);

  return (
    <div data-testid="reports-page">
      <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Reports</p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight">Licensing & audit exports</h1>
      <p className="mt-3 text-muted-foreground max-w-2xl">
        Pick a date range, preview the numbers, then download a spreadsheet you can hand straight to a
        licensing inspector or voucher agency.
      </p>

      <div className="mt-8 flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-sm font-bold">From</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="report-start" />
        </div>
        <div>
          <Label className="text-sm font-bold">To</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                 className="mt-2 h-11 rounded-xl w-44" data-testid="report-end" />
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="reports-list">
        {shown.map((r) => (
          <div key={r.key} className="bg-white rounded-2xl border border-border p-6 flex flex-col"
               data-testid={`report-card-${r.key}`}>
            <span className={`h-12 w-12 rounded-2xl grid place-items-center ${r.tone}`}>
              <r.icon size={24} weight="duotone" />
            </span>
            <h2 className="mt-5 font-display text-lg font-semibold">{r.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground flex-1">{r.blurb}</p>
            <p className="mt-3 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
              {r.ranged ? "Uses the date range" : "Current snapshot"}
            </p>
            <div className="mt-5 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => show(r)} disabled={busy === r.key}
                      className="rounded-full font-bold" data-testid={`preview-${r.key}`}>
                <Eye size={16} className="mr-1" /> Preview
              </Button>
              <Button size="sm" onClick={() => download(r)} disabled={busy === r.key}
                      className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                      data-testid={`download-${r.key}`}>
                <DownloadSimple size={16} className="mr-1" /> CSV
              </Button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div className="mt-10" data-testid="report-preview">
          <div className="flex flex-wrap justify-between items-end gap-4">
            <div>
              <h2 className="font-display text-2xl font-semibold">{preview.report.title}</h2>
              {preview.data.start && (
                <p className="mt-1 text-sm text-muted-foreground font-semibold">
                  {preview.data.start} → {preview.data.end}
                </p>
              )}
            </div>
            <Button variant="ghost" onClick={() => setPreview(null)} className="rounded-full font-bold"
                    data-testid="close-preview">Close preview</Button>
          </div>

          <div className="mt-5 flex flex-wrap gap-3" data-testid="report-totals">
            {totals.map(([k, v]) => (
              <span key={k} className="bg-white border border-border rounded-2xl px-5 py-3">
                <span className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">
                  {k.replace(/_/g, " ")}
                </span>
                <span className="ml-3 font-display text-lg font-bold">
                  {/amount|billed|collected|outstanding|paid/.test(k) ? money(v) : v}
                </span>
              </span>
            ))}
          </div>

          <div className="mt-5 bg-white rounded-2xl border border-border overflow-x-auto">
            {rows.length === 0 ? (
              <p className="p-8 text-muted-foreground" data-testid="report-empty">
                No records in this range yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {cols.map((c) => (
                      <th key={c} className="text-left p-4 font-bold uppercase tracking-[0.1em] text-xs whitespace-nowrap">
                        {c.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 50).map((r, i) => (
                    <tr key={cols.map((c) => r[c]).join("|") || i}
                        className="border-b border-border last:border-0" data-testid={`report-row-${i}`}>
                      {cols.map((c) => (
                        <td key={c} className="p-4 whitespace-nowrap">
                          {typeof r[c] === "boolean" ? (r[c] ? "Yes" : "") : String(r[c] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {rows.length > 50 && (
            <p className="mt-3 text-sm text-muted-foreground">
              Showing the first 50 of {rows.length} rows — the CSV contains them all.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
